import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { crmSettingsApi } from "../../settings/api/crmSettings";
import { getUserPermissions } from "../../../shared/lib/auth/authHelper";
import styles from "../../settings/pages/crmSettings/CRMSettingsPage.module.css";

function normalizePermissionName(permission) {
  return String(permission || "").trim().toLowerCase();
}

export default function WorkSchedulePage() {
  const { t, i18n } = useTranslation();
  const [config, setConfig] = useState({ enabled: false, mode: "recurring", status: null });
  const [rows, setRows] = useState([]);
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [flash, setFlash] = useState(null);

  const permissions = useMemo(
    () => (getUserPermissions() || []).map(normalizePermissionName),
    []
  );
  const canManage = permissions.includes("work_schedule.manage");

  const locale = useMemo(() => {
    const lng = String(i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
    if (lng.startsWith("ru")) return "ru-RU";
    if (lng.startsWith("et") || lng === "ee") return "et-EE";
    return "en-GB";
  }, [i18n.resolvedLanguage, i18n.language]);

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const groupedRows = useMemo(() => {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const map = new Map();
    rows.filter((row) => row.date >= today).forEach((row) => {
      if (!map.has(row.date)) map.set(row.date, []);
      map.get(row.date).push(row);
    });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, items]) => ({
        date,
        items: items.sort((a, b) => a.time.localeCompare(b.time)),
      }));
  }, [rows]);

  const previewCount = (preview?.items || []).reduce(
    (sum, item) => sum + (item.times?.length || 0),
    0
  );

  const refresh = async () => {
    setLoading(true);
    try {
      const [configData, slotData] = await Promise.all([
        crmSettingsApi.getWorkScheduleConfig(),
        crmSettingsApi.getWorkScheduleSlots(),
      ]);
      setConfig(configData || { enabled: false, mode: "recurring", status: null });
      setRows(slotData?.items || []);
      if (slotData?.status) {
        setConfig((current) => ({ ...current, status: slotData.status }));
      }
    } catch (error) {
      console.error(error);
      setFlash({ type: "error", text: t("work_schedule.action_error") });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doPreview = async () => {
    if (!canManage || !text.trim()) return;
    setFlash(null);
    setBusy("preview");
    try {
      setPreview(await crmSettingsApi.previewWorkSchedule(text));
    } catch (error) {
      console.error(error);
      setFlash({ type: "error", text: t("work_schedule.preview_error") });
    } finally {
      setBusy("");
    }
  };

  const doImport = async () => {
    if (!canManage || !preview?.items?.length) return;
    setFlash(null);
    setBusy("import");
    try {
      const data = await crmSettingsApi.importWorkSchedule({
        text,
        import_mode: "add",
        collision_mode: "add",
      });
      setRows(data.items || []);
      setConfig((current) => ({ ...current, status: data.status || current.status }));
      setPreview(null);
      setText("");
      setFlash({ type: "ok", text: t("work_schedule.import_success") });
      await refresh();
    } catch (error) {
      console.error(error);
      setFlash({ type: "error", text: t("work_schedule.action_error") });
    } finally {
      setBusy("");
    }
  };

  const addOne = async () => {
    if (!canManage || !newDate || !newTime) return;
    setFlash(null);
    setBusy("add");
    try {
      await crmSettingsApi.addWorkScheduleSlot({
        schedule_date: newDate,
        start_time: newTime,
      });
      setNewTime("");
      setFlash({ type: "ok", text: t("work_schedule.add_success") });
      await refresh();
    } catch (error) {
      console.error(error);
      setFlash({ type: "error", text: t("work_schedule.action_error") });
    } finally {
      setBusy("");
    }
  };

  const saveEdit = async () => {
    if (!canManage || !editing?.id || !editing.date || !editing.time) return;
    setFlash(null);
    setBusy(`edit-${editing.id}`);
    try {
      await crmSettingsApi.updateWorkScheduleSlot(editing.id, {
        schedule_date: editing.date,
        start_time: editing.time,
      });
      setEditing(null);
      setFlash({ type: "ok", text: t("work_schedule.update_success") });
      await refresh();
    } catch (error) {
      console.error(error);
      setFlash({ type: "error", text: t("work_schedule.action_error") });
    } finally {
      setBusy("");
    }
  };

  const deleteOne = async (id) => {
    if (!canManage || !window.confirm(t("work_schedule.confirm_delete"))) return;
    setFlash(null);
    setBusy(`delete-${id}`);
    try {
      await crmSettingsApi.deleteWorkScheduleSlot(id);
      setFlash({ type: "ok", text: t("work_schedule.delete_success") });
      await refresh();
    } catch (error) {
      console.error(error);
      setFlash({ type: "error", text: t("work_schedule.action_error") });
    } finally {
      setBusy("");
    }
  };

  const disabledForMode = config.mode !== "custom_dates";
  const disabledForUser = !canManage || disabledForMode || !config.enabled;

  return (
    <div className={`${styles.page} ${styles.workScheduleStandalonePage}`}>
      <div className={styles.workScheduleStandaloneHeader}>
        <div className={styles.workScheduleStandaloneHeaderCopy}>
          <span className={styles.workScheduleStandaloneEyebrow}>
            {t("work_schedule.menu_label")}
          </span>
          <h1 className={styles.workScheduleStandaloneTitle}>
            {t("work_schedule.user_page_title")}
          </h1>
          <p className={styles.workScheduleStandaloneSubtitle}>
            {t("work_schedule.user_page_subtitle")}
          </p>
        </div>
      </div>

      {!config.enabled ? (
        <div className={styles.workScheduleNotice}>
          <span className={styles.workScheduleNoticeIcon}>ⓘ</span>
          <span>{t("work_schedule.user_disabled_notice")}</span>
        </div>
      ) : null}

      {disabledForMode ? (
        <div className={styles.workScheduleNotice}>
          <span className={styles.workScheduleNoticeIcon}>ⓘ</span>
          <span>{t("work_schedule.user_mode_notice")}</span>
        </div>
      ) : null}

      {!canManage ? (
        <div className={styles.workScheduleStandaloneInfo}>
          <span className={styles.workScheduleStandaloneInfoIcon}>i</span>
          <div>
            <strong>{t("work_schedule.read_only_title")}</strong>
            <span>{t("work_schedule.read_only_text")}</span>
          </div>
        </div>
      ) : null}

      <div className={`${styles.card} ${styles.workScheduleRoot}`}>
        <div className={styles.workScheduleSection}>
          <div className={styles.workScheduleSectionHeader}>
            <div className={styles.workScheduleSectionTitle}>
              {t("work_schedule.quick_add_title")}
            </div>
            <div className={styles.workScheduleSectionHelp}>
              {t("work_schedule.quick_add_help")}
            </div>
          </div>
          <div className={styles.workScheduleSectionBody}>
            <div className={styles.workScheduleStack}>
              <div className={styles.workScheduleField}>
                <label className={styles.workScheduleLabel}>
                  {t("work_schedule.import_format")}
                </label>
                <textarea
                  className={styles.workScheduleControl}
                  rows={7}
                  value={text}
                  disabled={disabledForUser}
                  onChange={(e) => {
                    setText(e.target.value);
                    setPreview(null);
                  }}
                  placeholder={t("work_schedule.import_example")}
                />
                <div className={styles.workScheduleHelp}>
                  {t("work_schedule.user_import_help")}
                </div>
              </div>

              <div className={styles.workScheduleActions}>
                <button
                  type="button"
                  className={styles.workScheduleButton}
                  onClick={doPreview}
                  disabled={disabledForUser || !text.trim() || Boolean(busy)}
                >
                  {busy === "preview"
                    ? t("work_schedule.checking")
                    : t("work_schedule.preview_btn")}
                </button>
                <button
                  type="button"
                  className={`${styles.workScheduleButton} ${styles.workSchedulePrimary}`}
                  onClick={doImport}
                  disabled={disabledForUser || !preview?.items?.length || Boolean(busy)}
                >
                  {busy === "import"
                    ? t("work_schedule.saving")
                    : t("work_schedule.save_import_btn")}
                </button>
              </div>

              {preview ? (
                <div className={styles.workScheduleImportPreview}>
                  <div className={styles.workScheduleImportPreviewHead}>
                    <div>
                      <div className={styles.workScheduleImportPreviewTitle}>
                        {t("work_schedule.preview_title")}
                      </div>
                      <div className={styles.workScheduleImportPreviewMsg}>
                        {preview.items?.length
                          ? t("work_schedule.preview_ready")
                          : t("work_schedule.preview_empty")}
                      </div>
                    </div>
                    <span className={styles.workSchedulePreviewCount}>{previewCount}</span>
                  </div>
                  {preview.items?.length ? (
                    <div className={styles.workSchedulePreviewRows}>
                      {preview.items.map((item) => (
                        <div key={item.date} className={styles.workSchedulePreviewRow}>
                          <div className={styles.workSchedulePreviewDate}>
                            {formatDate(item.date)}
                          </div>
                          <div className={styles.workSchedulePreviewTimes}>
                            {(item.times || []).map((time) => (
                              <span
                                className={styles.workScheduleChip}
                                key={`${item.date}-${time}`}
                              >
                                {time}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {preview.errors?.length ? (
                    <div className={styles.workScheduleErrorPanel}>
                      <div className={styles.workScheduleErrorTitle}>
                        {t("work_schedule.import_errors")}
                      </div>
                      {preview.errors.map((item, index) => (
                        <div
                          className={styles.workScheduleErrorLine}
                          key={`${item.line}-${index}`}
                        >
                          {t("work_schedule.line_error", {
                            line: item.line,
                            text: item.text,
                            error: item.error,
                          })}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className={styles.workScheduleSection}>
          <div className={styles.workScheduleSectionHeader}>
            <div className={styles.workScheduleSectionTitle}>
              {t("work_schedule.manual_title")}
            </div>
            <div className={styles.workScheduleSectionHelp}>
              {t("work_schedule.manual_help")}
            </div>
          </div>
          <div className={styles.workScheduleSectionBody}>
            <div className={styles.workScheduleGrid3}>
              <div className={styles.workScheduleField}>
                <label className={styles.workScheduleLabel}>
                  {t("work_schedule.date")}
                </label>
                <input
                  type="date"
                  className={styles.workScheduleControl}
                  value={newDate}
                  disabled={disabledForUser}
                  onChange={(e) => setNewDate(e.target.value)}
                />
              </div>
              <div className={styles.workScheduleField}>
                <label className={styles.workScheduleLabel}>
                  {t("work_schedule.time")}
                </label>
                <input
                  type="time"
                  className={styles.workScheduleControl}
                  value={newTime}
                  disabled={disabledForUser}
                  onChange={(e) => setNewTime(e.target.value)}
                />
              </div>
              <div className={styles.workScheduleActions}>
                <button
                  type="button"
                  className={`${styles.workScheduleButton} ${styles.workSchedulePrimary}`}
                  onClick={addOne}
                  disabled={disabledForUser || !newDate || !newTime || Boolean(busy)}
                >
                  {t("work_schedule.add_slot")}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.workScheduleSection}>
          <div className={styles.workScheduleSectionHeader}>
            <div className={styles.workScheduleScheduleTop}>
              <div>
                <div className={styles.workScheduleSectionTitle}>
                  {t("work_schedule.schedule_title")}
                </div>
                <div className={styles.workScheduleSectionHelp}>
                  {t("work_schedule.user_schedule_help")}
                </div>
              </div>
              <span
                className={`${styles.workScheduleScheduleStatus} ${
                  !config.status?.has_future_dates
                    ? styles.workScheduleScheduleStatusWarn
                    : ""
                }`}
              >
                {config.status?.configured_until
                  ? t("work_schedule.configured_until", {
                      date: formatDate(config.status.configured_until),
                    })
                  : t("work_schedule.no_future_dates")}
              </span>
            </div>
          </div>
          <div className={styles.workScheduleSectionBody}>
            {loading ? (
              <div className={styles.workSchedulePreviewEmpty}>
                {t("work_schedule.loading")}
              </div>
            ) : null}

            {!loading && !groupedRows.length ? (
              <div className={styles.workSchedulePreviewEmpty}>
                {t("work_schedule.no_future_help")}
              </div>
            ) : null}

            {!loading && groupedRows.length ? (
              <div className={styles.workScheduleScheduleDays}>
                {groupedRows.map(({ date, items }) => (
                  <div className={styles.workScheduleDayRow} key={date}>
                    <div className={styles.workScheduleDayDate}>{formatDate(date)}</div>
                    <div className={styles.workScheduleSlotList}>
                      {items.map((row) =>
                        editing?.id === row.id ? (
                          <div className={styles.workScheduleEditRow} key={row.id}>
                            <input
                              type="date"
                              className={styles.workScheduleControl}
                              value={editing.date}
                              onChange={(e) =>
                                setEditing((current) => ({
                                  ...current,
                                  date: e.target.value,
                                }))
                              }
                            />
                            <input
                              type="time"
                              className={styles.workScheduleControl}
                              value={editing.time}
                              onChange={(e) =>
                                setEditing((current) => ({
                                  ...current,
                                  time: e.target.value,
                                }))
                              }
                            />
                            <div className={styles.workScheduleActions}>
                              <button
                                type="button"
                                className={`${styles.workScheduleButton} ${styles.workSchedulePrimary}`}
                                onClick={saveEdit}
                                disabled={Boolean(busy)}
                              >
                                {t("work_schedule.save")}
                              </button>
                              <button
                                type="button"
                                className={`${styles.workScheduleButton} ${styles.workScheduleGhost}`}
                                onClick={() => setEditing(null)}
                              >
                                {t("work_schedule.cancel")}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.workScheduleSlotItem} key={row.id}>
                            <span className={styles.workScheduleSlotTime}>{row.time}</span>
                            {canManage && !disabledForMode ? (
                              <>
                                <button
                                  type="button"
                                  className={styles.workScheduleIconBtn}
                                  title={t("work_schedule.edit")}
                                  aria-label={t("work_schedule.edit")}
                                  onClick={() =>
                                    setEditing({
                                      id: row.id,
                                      date: row.date,
                                      time: row.time,
                                    })
                                  }
                                >
                                  {t("work_schedule.edit")}
                                </button>
                                <button
                                  type="button"
                                  className={`${styles.workScheduleIconBtn} ${styles.workScheduleIconDanger}`}
                                  title={t("work_schedule.delete")}
                                  aria-label={t("work_schedule.delete")}
                                  disabled={busy === `delete-${row.id}`}
                                  onClick={() => deleteOne(row.id)}
                                >
                                  {t("work_schedule.delete")}
                                </button>
                              </>
                            ) : null}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {flash ? (
          <div
            className={`${styles.workScheduleFlash} ${
              flash.type === "ok"
                ? styles.workScheduleFlashOk
                : styles.workScheduleFlashError
            }`}
            style={{ marginTop: 14 }}
          >
            {flash.text}
          </div>
        ) : null}
      </div>
    </div>
  );
}
